import React, { useState } from 'react';
import { X, User, Save, Flame, Droplets, Mountain, Wind, Users, Plus, Trash2, Search, CheckCircle2, AlertCircle } from 'lucide-react';
import { THEMES } from '../../utils/constants';

export const ProfileModal = ({ data, onClose, onSave, onAddFriend, onRemoveFriend }) => {
    const [activeTab, setActiveTab] = useState('identity'); // 'identity' | 'circle'
    
    // --- IDENTITY STATE ---
    const [username, setUsername] = useState(data.username || '');
    const [avatar, setAvatar] = useState(data.elementalAvatar || 'air');
    const [loading, setLoading] = useState(false);

    // --- FRIEND STATE ---
    const [friendInput, setFriendInput] = useState('');
    const [friendStatus, setFriendStatus] = useState(null); // { type: 'success' | 'error', msg: '' }
    const [friendLoading, setFriendLoading] = useState(false);

    // --- HANDLERS ---
    const handleSaveProfile = async () => {
        if (!username.trim()) {
            alert("Please enter a username.");
            return;
        }
        setLoading(true);
        await onSave({ username, elementalAvatar: avatar });
        setLoading(false);
        // Don't close immediately, show success or let them move to friends
        alert("Identity Updated!");
    };

    const handleAddFriend = async () => {
        if (!friendInput.trim()) return;
        setFriendLoading(true);
        setFriendStatus(null);
        
        const result = await onAddFriend(friendInput.trim());
        
        setFriendStatus({ 
            type: result.success ? 'success' : 'error', 
            msg: result.message 
        });
        
        if (result.success) setFriendInput("");
        setFriendLoading(false);
    };

    // --- HELPERS ---
    const avatars = [
        { id: 'air', icon: Wind, label: 'Air', color: 'text-cyan-400', bg: 'bg-cyan-900/20', border: 'border-cyan-500' },
        { id: 'earth', icon: Mountain, label: 'Earth', color: 'text-emerald-400', bg: 'bg-emerald-900/20', border: 'border-emerald-500' },
        { id: 'water', icon: Droplets, label: 'Water', color: 'text-blue-400', bg: 'bg-blue-900/20', border: 'border-blue-500' },
        { id: 'fire', icon: Flame, label: 'Fire', color: 'text-orange-400', bg: 'bg-orange-900/20', border: 'border-orange-500' },
    ];

    const getAvatarIcon = (element) => {
        switch(element) {
            case 'fire': return <Flame className="w-4 h-4 text-orange-400" />;
            case 'water': return <Droplets className="w-4 h-4 text-blue-400" />;
            case 'earth': return <Mountain className="w-4 h-4 text-emerald-400" />;
            default: return <Wind className="w-4 h-4 text-cyan-400" />;
        }
    };

    return (
        <div className="fixed inset-0 z-[150] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-slate-950 border border-slate-800 w-full max-w-md rounded-2xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden">
                
                {/* Header */}
                <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-slate-900/50">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        {activeTab === 'identity' ? <User className="w-5 h-5 text-indigo-400" /> : <Users className="w-5 h-5 text-emerald-400" />}
                        {activeTab === 'identity' ? 'Identity' : 'Your Circle'}
                    </h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-slate-800">
                    <button 
                        onClick={() => setActiveTab('identity')}
                        className={`flex-1 py-3 text-sm font-bold transition-colors ${activeTab === 'identity' ? 'bg-slate-900 text-white border-b-2 border-indigo-500' : 'text-slate-500 hover:text-slate-300'}`}
                    >
                        Profile
                    </button>
                    <button 
                        onClick={() => setActiveTab('circle')}
                        className={`flex-1 py-3 text-sm font-bold transition-colors ${activeTab === 'circle' ? 'bg-slate-900 text-white border-b-2 border-emerald-500' : 'text-slate-500 hover:text-slate-300'}`}
                    >
                        Friends ({data.friends?.length || 0})
                    </button>
                </div>

                {/* Content Area */}
                <div className="p-6 overflow-y-auto">
                    
                    {/* --- TAB: IDENTITY --- */}
                    {activeTab === 'identity' && (
                        <div className="space-y-6">
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Codename (Username)</label>
                                <input 
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-xl p-3 text-white focus:border-indigo-500 outline-none font-bold tracking-wide"
                                    placeholder="e.g. FireWalker99"
                                    maxLength={15}
                                />
                                <p className="text-[10px] text-slate-500 mt-2">This is how friends will find you.</p>
                            </div>

                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase mb-3 block">Choose your Affinity</label>
                                <div className="grid grid-cols-4 gap-2">
                                    {avatars.map(opt => {
                                        const Icon = opt.icon;
                                        const isSelected = avatar === opt.id;
                                        return (
                                            <button 
                                                key={opt.id}
                                                onClick={() => setAvatar(opt.id)}
                                                className={`flex flex-col items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${isSelected ? `${opt.bg} ${opt.border}` : 'bg-slate-900 border-slate-800 opacity-50 hover:opacity-100'}`}
                                            >
                                                <Icon className={`w-6 h-6 ${opt.color}`} />
                                                <span className={`text-[10px] font-bold ${isSelected ? 'text-white' : 'text-slate-500'}`}>{opt.label}</span>
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>

                            <button 
                                onClick={handleSaveProfile}
                                disabled={loading}
                                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 mt-4"
                            >
                                <Save className="w-4 h-4" /> {loading ? "Saving..." : "Update Identity"}
                            </button>
                        </div>
                    )}

                    {/* --- TAB: CIRCLE --- */}
                    {activeTab === 'circle' && (
                        <div className="space-y-6">
                            
                            {/* Add Friend Input */}
                            <div className="bg-slate-900 p-4 rounded-xl border border-slate-800">
                                <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Add to Circle</label>
                                <div className="flex gap-2">
                                    <div className="relative flex-1">
                                        <Search className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
                                        <input 
                                            value={friendInput}
                                            onChange={(e) => setFriendInput(e.target.value)}
                                            className="w-full bg-slate-950 border border-slate-700 rounded-lg py-2 pl-10 pr-4 text-white text-sm focus:border-emerald-500 outline-none"
                                            placeholder="Enter Friend's Codename"
                                        />
                                    </div>
                                    <button 
                                        onClick={handleAddFriend}
                                        disabled={friendLoading || !friendInput}
                                        className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-800 disabled:text-slate-500 text-white px-4 rounded-lg font-bold transition-colors"
                                    >
                                        {friendLoading ? "..." : <Plus className="w-5 h-5" />}
                                    </button>
                                </div>
                                
                                {/* Feedback Message */}
                                {friendStatus && (
                                    <div className={`mt-3 text-xs flex items-center gap-2 ${friendStatus.type === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {friendStatus.type === 'success' ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                                        {friendStatus.msg}
                                    </div>
                                )}
                            </div>

                            {/* Friend List */}
                            <div>
                                <h3 className="text-xs font-bold text-slate-500 uppercase mb-3">Your Allies</h3>
                                <div className="space-y-2">
                                    {(data.friends || []).length === 0 ? (
                                        <div className="text-center p-6 border-2 border-dashed border-slate-800 rounded-xl text-slate-600">
                                            <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                            <p className="text-xs">No allies yet. Add someone to start.</p>
                                        </div>
                                    ) : (
                                        (data.friends || []).map((friend, idx) => (
                                            <div key={idx} className="flex items-center justify-between p-3 bg-slate-900 border border-slate-800 rounded-xl hover:border-slate-700 transition-colors">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center">
                                                        {getAvatarIcon(friend.elementalAvatar)}
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-bold text-white">{friend.username}</p>
                                                        <p className="text-[10px] text-slate-500">Connected {new Date(friend.addedAt).toLocaleDateString()}</p>
                                                    </div>
                                                </div>
                                                <button 
                                                    onClick={() => onRemoveFriend(friend.username)}
                                                    className="p-2 text-slate-600 hover:text-red-400 hover:bg-slate-800 rounded-full transition-colors"
                                                    title="Remove Friend"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};